import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import {
    DrawingDisplayType,
    DrawingMode,
    DrawingOptions,
    DrawingSettingGroup,
    DrawingSettingItem,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from './drawing.interface'
import DrawingPrimitive from './drawing.primitive'
import {
    AutoscaleInfo,
    BarPrice,
    Coordinate,
    IChartApi,
    ISeriesApi,
    Logical,
    MouseEventParams,
    PrimitiveHoveredItem,
    Time
} from 'lightweight-charts'
import DrawingPaneView from './drawing.paneView'
import DrawingHandlePaneView from './drawing.handle.paneView'
import { HANDLER_RADIUS, HANDLER_STROKE_WIDTH, SNAP_THRESHOLD } from './drawing.handler.constants'
import { determineChartX, determineChartY, scaledCoordinate } from './drawing.helper'
import { deepClone } from '@mui/x-data-grid/utils/utils'
import DrawingPaneAxisViewManager from './drawing.paneAxisViewManager'
import DrawingAxisLabelView from './drawing.axisLabelView'
import Chart from '../chart'
import { ChartEvents } from '../chart.events'
import { debounce } from 'throttle-debounce'
import { getTimeIndex } from '../../../util/chart'
import { convertToUTC } from '../../../util/dateTime'
import {ActionType} from "../../UndoRedoFunctonalities/UndoRedo.context";
import { primaryDrawingColor, white } from '../../../assets/colors'


class DrawingBase<O extends Record<string, any>, C extends Record<string, any>, S extends Record<string, any>> {
    id: string
    displayType: DrawingDisplayType
    referenceDrawingId?: string
    options: O
    _state: S
    _active = false
    _softActiveMode = false
    fixedDrawing = false;
    visible = true
    _chart: Chart
    _chartApi: IChartApi
    _seriesId = ''
    _paneId = ''
    _seriesMap: Record<string, ISeriesApi<any>> = {}
    _settings: C
    _paneElement?: HTMLElement
    linked: boolean
    mode?: DrawingMode
    editing: boolean = false
    magnetMode: boolean
    primitive?: DrawingPrimitive
    primitiveAttached = false
    _destroyed = false
    paneViews: DrawingPaneView[] = []
    resizeObserver: ResizeObserver
    _firstClick = true
    _hovered: boolean = false
    _chartBounds?: DOMRect
    _mousePressed = false
    activeElementId = ''
    moveStartMouseLocation?: PointCoordinates
    moveStartHandles?: Point[]
    axisViewManager: DrawingPaneAxisViewManager
    availableSettings: Record<string, DrawingSettingItem<C, SettingValues>> = {}
    lastAddedPoint?: Point
    previewCursorPosition?: PointCoordinates
    paneIndex: number = 0
    subscribedPaneIndex: number | undefined = undefined
    /** DRAW mode: mousedown/mouseup (or touch) on each pane so clicks work without relying on crosshair paneIndex alone */
    private _drawModePanePointerCleanups: Array<() => void> = []
    onMouseDownTime = new Date()
    onDestroy = (id: string, publish?: boolean) => {
    }
    onFocusChange = (id: string, focused: boolean) => {
    }
    onDrawingInactive = () => {
    }
    onDrawingActive = (drawingType: DrawingTypes) => {
    }
    onDrawingComplete = (id: string) => {
    }
    onDrawingUpdate = (id: string) => {
    }
    canvasWidth = 0
    canvasHeight = 0
    horizontalPixelRatio = 0
    verticalPixelRatio = 0
    private _cachedPrimarySeries: ISeriesApi<any> | null = null;
    private _cacheInvalidated = true;
    private _lastChartCompareMode: boolean | undefined = undefined;

    constructor({
                    id,
                    displayType,
                    referenceDrawingId,
                    options,
                    chart,
                    settings,
                    initialState,
                    magnetMode,
                    paneId,
                    seriesId,
                    onDestroy,
                    onFocusChange,
                    onDrawingInactive,
                    onDrawingActive,
                    onDrawingComplete,
                    onDrawingUpdate
                }: DrawingOptions<O, C, S>) {
        this.displayType = displayType;
        this.options = { ...options }
        this.id = id
        this.referenceDrawingId = referenceDrawingId
        this.linked = !!referenceDrawingId
        this._chart = chart
        this.magnetMode = magnetMode
        this._chartApi = chart.chartApi
        this._state = initialState
        this._paneId = paneId || ''
        this._seriesId = seriesId || ''
        this.resizeObserver = new ResizeObserver(this.onPaneResize)



        if (this._paneId && this._seriesId && this._chartApi) {
            const api = this._chart.getSeriesApi(this._paneId, this._seriesId)
            if (!api) {
                console.warn('DrawingBase: Series not found for drawing', this.id, 'paneId:', this._paneId, 'seriesId:', this._seriesId)
            } else {
                this._seriesMap[this.id] = api
                this.setPaneElement(this._paneId)
            }
        }
        if (Object.keys(initialState).length) {
            this.mode = 'EDIT'
        }
        this.onDestroy = onDestroy
        this.onFocusChange = onFocusChange
        this.onDrawingInactive = onDrawingInactive
        this.onDrawingActive = onDrawingActive
        this.onDrawingComplete = onDrawingComplete
        this.onDrawingUpdate = onDrawingUpdate
        this.primitive = new DrawingPrimitive({
            drawing: this
        })
        this.paneViews = [new DrawingPaneView({
            drawing: this,
            id: 'primary'
        })]
        this.axisViewManager = new DrawingPaneAxisViewManager({ drawing: this })
        this._chartApi && this.mode == 'EDIT' && this.subscribeClick()
        this._chartApi && this.mode == 'EDIT' && this.subscribeDblClick()
        this._firstClick = this.referenceDrawingId ? false : true
        this.addMouseLeaveHandler()
        this._settings = {} as C
        setTimeout(() => {
            if (!this._chartApi || (this._chart as any).destroyed || !this.primitive) return
            this._settings = this._createSettings({
                ...settings
            })
            if (this.displayType === DrawingDisplayType.INDICATOR) {
                this.onSettingsUpdate(this._settings)
            }
            if (this._paneId && this._seriesId) {
                this.ensurePrimitiveAttachedToSeries()
                this.updateDrawing()
            }
            if(this.mode === 'EDIT') {
                this.loadIntialState()
            }
        }, 0)
        this.onMouseDownTime = new Date()
        this._invalidateSeriesCache()
    }

    /**
     * Resolves `getSeriesApi` after pane/series init and attaches the primitive once.
     * Retries briefly for `fixedDrawing` tools (e.g. chart alerts) when the series is not ready on the first tick.
     */
    private ensurePrimitiveAttachedToSeries(attempt = 0): void {
        if (
            this._destroyed ||
            !this._chartApi ||
            (this._chart as any).destroyed ||
            !this.primitive ||
            !this._paneId ||
            !this._seriesId
        )
            return
        if (!this.getSeries()) {
            const api = this._chart.getSeriesApi(this._paneId, this._seriesId)
            if (api) {
                this._seriesMap[this.id] = api
                this.setPaneElement(this._paneId)
            }
        }
        const series = this.getSeries()
        if (series && this.primitive && !this.primitiveAttached) {
            try {
                series.attachPrimitive(this.primitive)
                this.primitiveAttached = true
            } catch (e) {
                console.warn('DrawingBase: Failed to attach primitive, chart may have been destroyed', e)
            }
        } else if (!series && this.fixedDrawing && attempt < 15) {
            setTimeout(() => this.ensurePrimitiveAttachedToSeries(attempt + 1), 80)
        }
    }

    _createSettings = (config: Partial<C>) => {

        const availableSettings = this.getAvailableSettings()

        const settings: C = availableSettings.reduce((acc, group) => {
            group.settings.forEach((setting) => {
                if (acc[setting.name]) throw `Duplicate setting name ${setting.name.toString()}`
                acc[setting.name] = setting.defaultValue
            })
            return {
                ...acc
            }
        }, {} as any)
        return {
            ...settings,
            ...config
        }
    }

    activate() {
        if (!this.mode) {
            this.mode = 'DRAW'
            if (this.referenceDrawingId) return
            this._onDrawingActive()
            if (!this._chartApi) return
            if (this._chart.touchEnabled) {
                this._chartApi.subscribeClick(this._onMouseMovePaneIndexTracking)
                this._onMouseMovePaneIndexTracking({ paneIndex: this.paneIndex || 0 } as any)
            } else {
                this._chartApi.subscribeCrosshairMove(this._onMouseMovePaneIndexTracking)
            }
            this.addMouseMoveHandler()
            this._attachDrawModePointerListeners()
        }
    }

    deactivate() {
        if (this.mode === 'DRAW') {
            this.mode = undefined
            this._chart.updateLayoutOptions({
                handleScroll: true
            })
            this.destroy()
        }
    }

    _getHandles = (): Point[] => {
        if (!this._active && this.mode === 'EDIT') return []
        return this.getHandles()
    }

    getPrimitive() {
        return this.primitive
    }

    setPaneElement(paneId: string, paneElement?: HTMLElement) {
        if (paneElement) {
            this._paneElement = paneElement
        } else {
            this._paneElement = this._chart.getPaneElementById(paneId)
        }
        if (!this._chartBounds && this._paneElement) this._chartBounds = this?._paneElement?.getBoundingClientRect()
        if (this._chartBounds) {
            this.canvasWidth = this._chartBounds.width * window.devicePixelRatio
            this.canvasHeight = this._chartBounds.height * window.devicePixelRatio
        }
        if (this._paneElement instanceof Element) {
            this.resizeObserver.observe(this._paneElement);
        } else {
            console.warn("ResizeObserver: tried to observe invalid element:", this._paneElement);
        }
    }

    onPaneResize = debounce(100, (entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
            this._chartBounds = entry.target.getBoundingClientRect()
        }
    })

    subscribeVisibleLogicalRange() {
        if (!this._chartApi) return
        this._chartApi.timeScale().subscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    unsubscribeVisibleLogicalRange() {
        if (!this._chartApi) return
        this._chartApi.timeScale().unsubscribeVisibleLogicalRangeChange(this.onVisibleLogicalRangeChange)
    }

    subscribeClick() {
        if (!this._chartApi) return
        this._chartApi.subscribeClick(this._onClick)
    }

    unsubscribeDblClick() {
        if (!this._chartApi) return
        this._chartApi.unsubscribeDblClick(this._onDblClick)
    }

    subscribeDblClick() {
        if (!this._chartApi) return
        this._chartApi.subscribeDblClick(this._onDblClick)
    }

    unsubscribeClick() {
        if (!this._chartApi) return
        this._chartApi.unsubscribeClick(this._onClick)
    }

    addMouseLeaveHandler() {
        if (this.getPaneElement()) {
            this.getPaneElement().addEventListener('mouseleave', this._onMouseLeave)
            const children = this.getPaneElement().children
            for (let i = 0; i < children.length; i++) {
                children.item(i)?.addEventListener('mouseenter', this._onMouseLeave)
            }
        }
    }

    removeMouseLeaveHandler() {
        if (this.getPaneElement()) {
            this.getPaneElement().removeEventListener('mouseleave', this._onMouseLeave)
            const children = this.getPaneElement().children
            for (let i = 0; i < children.length; i++) {
                children.item(i)?.removeEventListener('mouseenter', this._onMouseLeave)
            }
        }
    }

    addMouseMoveHandler() {
        if (!this._chartApi) return
        this._chartApi.subscribeCrosshairMove(this._onMouseMove)
        // this.getPaneElement().addEventListener('mousemove', this._onMouseMove)
    }

    removeMouseMoveHandler() {
        if (!this._chartApi) return
        this._chartApi.unsubscribeCrosshairMove(this._onMouseMove)
        // this.getPaneElement().removeEventListener('mousemove', this._onMouseMove)
    }

    addMouseDownHandler() {
        if(this._chart.touchEnabled) {
            this.getPaneElement() && this.getPaneElement().addEventListener('touchstart', this._onMouseDown, { passive: true })
        } else {
            this.getPaneElement() && this.getPaneElement().addEventListener('mousedown', this._onMouseDown)
        }
    }

    removeMouseDownHandler() {
        if(this._chart.touchEnabled) {
            this.getPaneElement() && this.getPaneElement().removeEventListener('touchstart', this._onMouseDown)
        } else {
            this.getPaneElement() && this.getPaneElement().removeEventListener('mousedown', this._onMouseDown)
        }
    }

    addGlobalMouseUpHandler() {
        if(this._chart.touchEnabled) {
            document.addEventListener('touchend', this._globalOnMouseUp, { passive: true })
        } else {
            document.addEventListener('mouseup', this._globalOnMouseUp)
        }
    }

    removeGlobalMouseUpHandler() {
        if(this._chart.touchEnabled) {
            document.removeEventListener('touchend', this._globalOnMouseUp)
        } else {
            document.removeEventListener('mouseup', this._globalOnMouseUp)
        }
    }

    onVisibleLogicalRangeChange = () => {
        this.updateDrawing()
    }
    private _onMouseMove = (event: MouseEventParams<Time>) => {
        this.onMouseMove(event)
    }

    private _onMouseLeave = () => {
        this.hitTest(NaN, NaN)
    }

    toggleFocus(focus: boolean, publish = true, softActiveMode = false) {
        this._active = focus
        this._softActiveMode = softActiveMode
        this.updateDrawing(false)
        !this._softActiveMode && this._active && this.addMouseDownHandler()
        !this._softActiveMode && !this._active && this.removeMouseDownHandler()
        publish && this._chart.publishDrawingEvent('focused', {
            id: this.id,
            focused: focus,
            referenceId: this.referenceDrawingId
        }, true)
        publish && this.onFocusChange(this.id, this._active)
    }

    getSeries() {
        return this._seriesMap[this.id]
    }

    getPaneElement(): HTMLElement {
        return this._paneElement!
    }

    private _onDblClick = (event: MouseEventParams) => {
        this._onClick(event, true)
        if (this.mode === 'EDIT') {
            this.openDrawingSettings()
        }
    }

    /**
     * Align pane element, series, and (before the first point) the drawing primitive with the given pane index.
     * Re-attaches the primitive when the user moves or clicks a different pane before placing the first point.
     */
    private _syncDrawPaneFromIndex(paneIndex: number): boolean {
        if (!this._chartApi) return false
        const paneId = this._chart.getPaneIdByIndex(paneIndex)
        const paneElements = this._chartApi.getPaneElements()
        const paneElement = paneElements[paneIndex]
        if (!paneId || !paneElement) return false
        this.paneIndex = paneIndex
        this.setPaneElement(paneId, paneElement)

        if (this.mode !== 'DRAW' || !this._firstClick || !this.primitive) {
            return true
        }

        const [seriesId, series] = this._chart.getDrawingSeries(paneId)
        if (!seriesId || !series) return true

        const alreadyOk =
            this.primitiveAttached &&
            this._paneId === paneId &&
            this._seriesId === seriesId &&
            this.getSeries() === series
        if (alreadyOk) return true

        const prevSeries = this.primitiveAttached ? this.getSeries() : undefined
        if (prevSeries && this.primitive) {
            try {
                prevSeries.detachPrimitive(this.primitive)
            } catch (e) {
                console.warn('DrawingBase: Failed to detach primitive when switching pane', e)
            }
            this.primitiveAttached = false
        }

        this._seriesId = seriesId
        this._paneId = paneId
        this._seriesMap[this.id] = series
        try {
            series.attachPrimitive(this.primitive)
            this.primitiveAttached = true
        } catch (e) {
            console.warn('DrawingBase: Failed to attach primitive', e)
        }
        return true
    }

    private _attachDrawModePointerListeners() {
        this._removeDrawModePointerListeners()
        if (!this._chartApi || this.mode !== 'DRAW') return
        const elements = [...(this._chartApi.getPaneElements() || [])] as HTMLElement[]
        elements.forEach((el, paneIndex) => {
            const onDown = (e: MouseEvent | TouchEvent) => {
                this._onMouseDownClick(e, paneIndex)
            }
            const onUp = (e: MouseEvent | TouchEvent) => {
                this._onMouseUpClick(e, paneIndex)
            }
            if (this._chart.touchEnabled) {
                el.addEventListener('touchstart', onDown, { passive: true })
                el.addEventListener('touchend', onUp, { passive: true })
                this._drawModePanePointerCleanups.push(() => {
                    el.removeEventListener('touchstart', onDown)
                    el.removeEventListener('touchend', onUp)
                })
            } else {
                el.addEventListener('mousedown', onDown)
                el.addEventListener('mouseup', onUp)
                this._drawModePanePointerCleanups.push(() => {
                    el.removeEventListener('mousedown', onDown)
                    el.removeEventListener('mouseup', onUp)
                })
            }
        })
    }

    private _removeDrawModePointerListeners() {
        this._drawModePanePointerCleanups.forEach((fn) => fn())
        this._drawModePanePointerCleanups = []
    }

    private _onMouseDownClick = (event: MouseEvent | TouchEvent, paneIndexFromEvent?: number) => {
        if (paneIndexFromEvent !== undefined) {
            this._syncDrawPaneFromIndex(paneIndexFromEvent)
        }
        const paneEl = this._paneElement
        if (!paneEl) return
        this._chartBounds = paneEl.getBoundingClientRect()
        this.onMouseDownTime = new Date()
        const touch = (event as TouchEvent)?.touches?.[0]
        const chartX = determineChartX(
            this._chartApi,
            this._chartBounds,
            this._chart.touchEnabled ? (touch?.clientX ?? 0) : ((event as MouseEvent).clientX ?? touch?.clientX ?? 0)
        )
        const chartY = determineChartY(
            this._chartApi,
            this._chartBounds,
            this._chart.touchEnabled ? (touch?.clientY ?? 0) : ((event as MouseEvent).clientY ?? touch?.clientY ?? 0)
        )
        if (this.mode === 'DRAW') {
            this._chart.updateLayoutOptions({
                handleScroll: {
                    pressedMouseMove: false
                }
            })
            const synthetic = {
                hoveredObjectId: undefined,
                hoveredSeries: undefined,
                logical: true,
                paneIndex: this.paneIndex,
                point: {
                    x: chartX,
                    y: chartY
                },
                seriesData: undefined,
                sourceEvent: undefined,
                time: this._chartApi.timeScale().coordinateToTime(chartX ?? 0)
            }
            this._onClick(synthetic as any, false)
        }
    }

    private _onMouseUpClick = (event: MouseEvent | TouchEvent, paneIndexFromEvent?: number) => {
        if (paneIndexFromEvent !== undefined) {
            this._syncDrawPaneFromIndex(paneIndexFromEvent)
        }
        this._chart.updateLayoutOptions({
            handleScroll: {
                pressedMouseMove: true
            }
        })

        // Add second point on mouse up when in drawing mode and first point exists
        // Only trigger for long drags (> 150ms), not for quick clicks
        if (this.mode === 'DRAW' && !this._firstClick && this.onMouseDownTime) {
            const currentTime = new Date()
            const timeDiff = currentTime.getTime() - this.onMouseDownTime.getTime()
            const isLongDrag = timeDiff > 150 // Consider it a long drag if more than 150ms

            if (isLongDrag && this._paneElement) {
                this._chartBounds = this._paneElement.getBoundingClientRect()
                const touch = (event as TouchEvent)?.touches?.[0] || (event as TouchEvent)?.changedTouches?.[0]
                const chartX = determineChartX(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientX ?? 0) : (event as MouseEvent).clientX)
                const chartY = determineChartY(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientY ?? 0) : (event as MouseEvent).clientY)

                if (chartX && chartY) {
                    const mouseUpEvent = {
                        hoveredObjectId: undefined,
                        hoveredSeries: undefined,
                        logical: true,
                        paneIndex: this.paneIndex,
                        point: {
                            x: chartX,
                            y: chartY
                        },
                        seriesData: undefined,
                        sourceEvent: undefined,
                        time: this._chartApi.timeScale().coordinateToTime(chartX ?? 0)
                    }
                    this._onClick(mouseUpEvent as any, false)
                }
            }
        }
    }

    private _onMouseMovePaneIndexTracking = (event: MouseEventParams<Time>) => {
        const paneIndex = event.paneIndex ?? 0
        this.paneIndex = paneIndex
        if (this.subscribedPaneIndex !== paneIndex) {
            this.subscribedPaneIndex = paneIndex
            this._syncDrawPaneFromIndex(paneIndex)
        }
    }

    private _onClick = (event: MouseEventParams, dblClick = false) => {
        if (this.mode === 'DRAW') {
            if (this.referenceDrawingId) return
            const paneIndex = event.paneIndex || 0
            const paneId = this._chart.getPaneIdByIndex(paneIndex)
            const paneElement = this._chartApi.getPaneElements()[paneIndex]
            if (!paneElement) return
            this.setPaneElement(paneId, paneElement)


            const [seriesId, series] = this._chart.getDrawingSeries(paneId)
            if (!seriesId || !series) return
            if (this._firstClick) {
                this._chart.publishDrawingEvent('abort', {
                    id: this.id
                }, true)
                const pane = this._chart.getPane(paneId)
                pane && pane.primary && this._chart.publishDrawingEvent('add', {
                    type: this.getDrawingType(),
                    id: this.id,
                    options: this.options,
                    settings: this._settings,
                    initialState: {}
                })
            }
            this._seriesId = seriesId
            this._paneId = paneId
            this._seriesMap[this.id] = series
            if (!event.point || !event.time || !event.logical) return
            let price = this.coordinateToPrice(event.point.y), eventPoint = { ...event.point }
            if (!price) return
            if (this.magnetMode) {
                price = this.getSnapPoint(event.time, price, event.point.y)
                eventPoint.y = (this.priceToCoordinate(price) || 0) as Coordinate
            }
            this._onAddPoint(event.time, price, eventPoint)
            if (this._firstClick) {
                if (!this.primitiveAttached && this.primitive) {
                    try {
                        series.attachPrimitive(this.primitive)
                        this.primitiveAttached = true
                    } catch (e) {
                        console.warn('DrawingBase: Failed to attach primitive', e)
                    }
                }
                this._firstClick = false
                this.previewCursorPosition = undefined
                !this.magnetMode && this.addMouseMoveHandler()
                this.updateDrawing()
            }
        } else if ((this._active !== (event.hoveredObjectId === this.id)) || this._softActiveMode) {
            this.toggleFocus(this._softActiveMode ? true : !this._active, this._softActiveMode || !this._active || !event.hoveredObjectId)
        } else if (this._chart.touchEnabled) {
            const item = this.hitTest(event.point.x, event.point.y)
            if(item && item.externalId === this.id) {
                this.toggleFocus(this._softActiveMode ? true : !this._active, this._softActiveMode || !this._active || !item.externalId)
            }
        }
        this._chart.publishEvent(ChartEvents.DRAWING_HOVERED, null)
    }

    _onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean, external = false): Point | void => {
        this._chart.publishDrawingEvent('add_point', {
            id: this.id,
            time,
            price,
            coordinates,
            preview
        })
        if (this._firstClick && external) this._firstClick = false
        const point = this.onAddPoint(time, price, coordinates, preview)
        if (!preview) {
            if (!point && this.mode === 'DRAW') throw 'Last added point must be returned'
            this.lastAddedPoint = point as Point
        }
        this.updateDrawing()
    }

    onDrawingEnd() {
        this.mode = 'EDIT'
        this.editing = false
        this._removeDrawModePointerListeners()
        if (this._chartApi) this._chartApi.unsubscribeCrosshairMove(this._onMouseMovePaneIndexTracking)
        if (this._chartApi) this._chartApi.unsubscribeClick(this._onMouseMovePaneIndexTracking)
        this.removeMouseMoveHandler()
        this.toggleFocus(true, !this.referenceDrawingId, !!this.referenceDrawingId)
        this.onDrawingInactive()
        this.onDrawingComplete(this.id)
        const data={
            chartId:this._chart.id,
            drawing: [{id: this.id,
            type:this.getDrawingType(),
            options: this.options,
            state: this._state,
            paneId:this._paneId,
            seriesId:this._seriesId,
            settings: this._settings,
            }]
        }
        this._chart.historyManager.push(data, `ADD DRAWING (${this.getDrawingType()})`, ActionType.ADD_DRAWING)
        setTimeout(() => {
            this.subscribeClick()
            this.subscribeDblClick()
        }, 200)
    }

    onMouseMove = (event: MouseEventParams<Time>) => {
        const chartX = event.point?.x //determineChartX(this._chartApi, this._chartBounds!, event.clientX);
        const chartY = event.point?.y //determineChartY(this._chartApi, this._chartBounds!, event.clientY);
        const paneIndex = event.paneIndex || 0
        const paneId = this._chart.getPaneIdByIndex(paneIndex)
        if (!chartX || !chartY || paneId !== this._paneId) return
        let price = this.coordinateToPrice(chartY as Coordinate)
        const time = this.coordinateToTime(chartX as Coordinate)
        if (!time || !price) return

        if (this.mode === 'DRAW' && this._firstClick) {
            const snappedX = this.timeToCoordinate(time)
            this.previewCursorPosition = { 
                x: snappedX || chartX, 
                y: chartY 
            }
        }

        // Snap to prices
        if (this.magnetMode) {
            price = this.getSnapPoint(time, price, chartY)

            this._chartApi.setCrosshairPosition(price!, time, this.getSeries())
        }
        if (this.mode === 'DRAW') {
            if (!this._firstClick) {
                this._onAddPoint(time, price!, { x: chartX, y: chartY }, true)
                this.updateDrawing()
            } else {
                this.updateDrawing()
            }
        } else if (this.activeElementId) {
            const [drawingId, handleId] = this.activeElementId.split('__')
            if (handleId) {
                this.editing = true
                this._updateHandle(handleId, time, price!, { x: chartX, y: chartY })
            } else if (drawingId === this.id) {
                this.editing = true
                this.moveDrawing(chartX, chartY)
            }
        }
    }

    _updateHandle(handleId: string, time: Time, price: number, coordinates: PointCoordinates, publish = true, syncMode = false) {
        this.updateHandle(handleId, time, price!, coordinates, syncMode)
        this.updateDrawing()
        this.onDrawingUpdate(this.id)
        publish && this._chart.publishDrawingEvent('update_handle', {
            id: this.id,
            handleId,
            time,
            price,
            coordinates,
            referenceId: this.referenceDrawingId
        }, true)
    }

    publishSyncStateEvent() {
        this._chart.publishDrawingEvent('update_state', {
            id: this.id,
            referenceId: this.referenceDrawingId,
            state: this.getSyncState()
        }, true)
    }

    getSnapPoint(time: Time, price: BarPrice, chartY: number) {
        const logical = this.timeToLogical(time)
        let snapPrice = price
        if (logical) {
            const data = this.getSeries().data()
            const candle = data[logical]
            if (candle) {
                const { open, high, low, close } = candle
                // opposite becuase of inverted canvas coordinates
                const max = this.priceToCoordinate(low) || 0
                const min = this.priceToCoordinate(high) || 0
                if (chartY >= min - SNAP_THRESHOLD && chartY <= max + SNAP_THRESHOLD) {
                    let updatePrice = close;
                    [open, high, low].forEach((p) => {
                        const y = this.priceToCoordinate(p) || 0
                        if (Math.abs(y - chartY) < Math.abs((this.priceToCoordinate(updatePrice) || 0) - chartY)) {
                            updatePrice = p
                        }
                    })
                    snapPrice = updatePrice
                }
            }
        }

        return snapPrice

    }

    _onMouseDown = (event: MouseEvent | TouchEvent) => {        
        this._chartBounds = this.getPaneElement().getBoundingClientRect()
        const touch = (event as TouchEvent)?.touches?.[0]
        const chartX = determineChartX(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientX??0) : ((event as MouseEvent)?.clientX??0))
        const chartY = determineChartY(this._chartApi, this._chartBounds, this._chart.touchEnabled ? (touch?.clientY??0) : ((event as MouseEvent)?.clientY??0))
        if (!chartX || !chartY) return
        this.moveStartMouseLocation = {
            x: chartX,
            y: chartY
        }
        this.moveStartHandles = deepClone(this.getHandles() as any)
        const hoveredItem = this._hitTest(chartX, chartY)
        if (hoveredItem) {
            this._mousePressed = true
            this.activeElementId = hoveredItem.externalId
            this._chartApi.applyOptions({
                handleScroll: {
                    pressedMouseMove: false
                }
            })
            this.addMouseMoveHandler()
            this.addGlobalMouseUpHandler()
        }
    }
    _globalOnMouseUp = () => {
        if(this.editing) {
            this.editing = false
            this.onEditEnding()
        }
        this._mousePressed = false
        this.activeElementId = ''
        this.moveStartMouseLocation = undefined
        this.moveStartHandles = undefined
        this._chartApi.applyOptions({
            handleScroll: {
                pressedMouseMove: true
            }
        })
        this.removeMouseMoveHandler()
        this.removeGlobalMouseUpHandler()
        this.onGlobalMouseUp();
    }

    getPaneAxisViewConfig(id: string) {
        return this.axisViewManager.getPaneAxisViewConfig(id)
    }

    getPaneViews = (): DrawingPaneView[] => {
        let paneViews = this.paneViews
        const paneViewIDs = paneViews.map(v => v.id)
        const handles = this._getHandles()
        handles.forEach((h) => {
            if (paneViewIDs.indexOf(h.id) === -1 && !h.hidden) {
                paneViews = [...paneViews, new DrawingHandlePaneView({ id: h.id, drawing: this })]
            }
        })
        return paneViews
    }
    getPriceAxisPaneViews = () => {
        return this.axisViewManager.getPriceAxisPaneViews()
    }
    getTimeAxisPaneViews = () => {
        return this.axisViewManager.getTimeAxisPaneViews()
    }

    getPriceAxisLabels(): DrawingAxisLabelView[] {
        return this.axisViewManager.getPriceAxisLabels()
    }

    getTimeAxisLabels(): DrawingAxisLabelView[] {
        return this.axisViewManager.getTimeAxisLabels()
    }

    moveDrawing = (x: number, y: number) => {
        if (!this.moveStartMouseLocation || !this.moveStartHandles) {
            return
        }

        const { x: startX, y: startY } = this.moveStartMouseLocation
        const xStartIndex = this._chartApi.timeScale().coordinateToLogical(startX)
        const currentIndex = this._chartApi.timeScale().coordinateToLogical(x)
        if (!currentIndex || !xStartIndex) return
        const deltaIndex = currentIndex - xStartIndex
        const deltaY = y - startY
        let update = true
        const updateHandles = this.moveStartHandles.map((h) => {

            const updatedHandle = this.getUpdatedPoint(h, deltaIndex, deltaY, this.getSeries())
            if (!updatedHandle) {
                update = false
                return h
            }
            return updatedHandle
        })

        if (update) {
            this._updateHandles(updateHandles)
        }
    }

    _updateHandles(points: Point[], publish = true) {
        this.updateHandles(points)
        this.updateDrawing()
        this.onDrawingUpdate(this.id)
        publish && this._chart.publishDrawingEvent('move', {
            id: this.id,
            handles: points,
            referenceId: this.referenceDrawingId
        }, true)
    }

    getUpdatedPoint(p: Point, deltaIndex: number, deltaY: number, series: ISeriesApi<any>): Point | null {
        const xCoord = p.coordinates ? p.coordinates.x : this.timeToCoordinate(p.x)
        let yCoord = p.coordinates ? p.coordinates.y : series.priceToCoordinate(p.y) as number
        if (!xCoord || !yCoord) return null
        const timescale = this._chartApi.timeScale()
        let xIndex = timescale.coordinateToLogical(xCoord) as number
        if (!xIndex) return null
        xIndex += deltaIndex

        yCoord += deltaY

        const newXCoord = timescale.logicalToCoordinate(xIndex as Logical)
        const newYCoord = series.coordinateToPrice(yCoord)
        if (!newXCoord || !newYCoord) return null
        const newTime = timescale.coordinateToTime(newXCoord)
        if (!newTime) return null

        const newPoint: Point = {
            ...p,
            x: newTime,
            y: newYCoord
        }

        if (p.coordinates) {
            newPoint.coordinates = {
                x: newXCoord,
                y: yCoord
            }
        }
        return newPoint
    }

    getHandlePoint(id: string) {
        return this.getHandles().find((v => v.id === id))
    }

    updateDrawing(recalculateHandles = true) {
        this._invalidateSeriesCache()
        recalculateHandles && this.recalculateHandles()
        this.axisViewManager.updateAxisConfigs()
        this.primitive?.requestUpdate()
    }

    _endDrawing(publish = true) {
        this.endDrawing()
        publish && this._chart.publishDrawingEvent('end', {
            id: this.id,
            referenceId: this.referenceDrawingId
        })
    }

    _hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        if (this.mode === 'DRAW') return null

        let hoveredItem: PrimitiveHoveredItem = {
            externalId: '',
            zOrder: 'normal',
            cursorStyle: 'pointer'
        }
        if (this._active) {
            const handleId = this._checkHandleHovered(x, y)
            if (handleId) {
                hoveredItem.externalId = handleId
            }
        }
        if (!hoveredItem.externalId) {
            let item = this.hitTest(x, y)
            if (item) {
                hoveredItem = item
            }
        }
        return hoveredItem.externalId ? {
            ...hoveredItem,
            cursorStyle: hoveredItem.cursorStyle ?? 'pointer'
        } : null
    }

    _checkHandleHovered(x: number, y: number): string {
        const handles = this.getHandles()
        let handleId = ''
        handles.forEach((h) => {
            let hx = this.timeToCoordinate(h.x)
            let hy = this.priceToCoordinate(h.y)

            if (h.coordinates) {
                hx = h.coordinates.x as Coordinate
                hy = h.coordinates.y as Coordinate
            }
            if (!hx || !hy) return
            if (Math.pow(hx - x, 2) + Math.pow(hy - y, 2) <= Math.pow(HANDLER_RADIUS + HANDLER_STROKE_WIDTH, 2) && !h.hidden) {
                handleId = this.id + '__' + h.id
            }
        })

        return handleId
    }

    getTimescaleData = (): Array<{time: Time}> => {
        return [...this._chart.getRenderedPrimaryData(), ...this._chart.emptyTimeSlots]
    }

    private _getPrimarySeriesForCoordinateConversion(): ISeriesApi<any> | null {
        // Check if compare mode changed (which affects which series to use)
        const currentCompareMode = this._chart.compareMode;
        if (this._lastChartCompareMode !== currentCompareMode) {
            this._invalidateSeriesCache();
            this._lastChartCompareMode = currentCompareMode;
        }

        // Use cached series if available and cache is valid
        if (!this._cacheInvalidated && this._cachedPrimarySeries) {
            return this._cachedPrimarySeries
        }
        // Try to get the primary series that participates in percentage scaling
        const primarySeriesGroup = this._chart.getPrimarySeriesGroup()
        if (primarySeriesGroup && primarySeriesGroup.series.length > 0) {
            const primarySeriesConfig = primarySeriesGroup.series[0]
            const primarySeries = primarySeriesGroup.chartSeries[primarySeriesConfig?.id]
            if (primarySeries && primarySeries.seriesApi) {
                this._cachedPrimarySeries = primarySeries.seriesApi
                this._cacheInvalidated = false
                return this._cachedPrimarySeries
            }
        }
        // Cache the blank series as fallback
        const blankSeries = this._chart.getBlankSeries()
        this._cachedPrimarySeries = blankSeries || null
        this._cacheInvalidated = false
        return this._cachedPrimarySeries
    }

    private _invalidateSeriesCache() {
        this._cacheInvalidated = true
        this._cachedPrimarySeries = null
    }

    coordinateToPrice(y: Coordinate) {
        const srs = this.getSeries() || this._getPrimarySeriesForCoordinateConversion()
        if (!srs) return null
        return srs.coordinateToPrice(y)
    }

    coordinateToTime(x: Coordinate) {
        const timeScale = this._chartApi.timeScale()
        return timeScale.coordinateToTime(x)
    }

    priceToCoordinate(price: number): Coordinate | null {
        const srs = this.getSeries() || this._getPrimarySeriesForCoordinateConversion()
        if (!srs) return null
        return srs.priceToCoordinate(price)
    }

    timeToLogical(time: Time): number | null {
        const timeScale = this._chartApi.timeScale()

        const coord = this.timeToCoordinate(time)

        if (!coord) return null

        return timeScale.coordinateToLogical(coord)
    }

    timeToCoordinate(time: Time, exact?: boolean): Coordinate | null {
        const timeScale = this._chartApi.timeScale()
        const data = this.getTimescaleData();
        if (!data.length) return null
        const index = getTimeIndex(data, time as number)
        if (!Number.isFinite(index)) return null

        const newTime1 = timeScale.logicalToCoordinate(index as Logical)
        if (!exact) {
            return newTime1
        }
        if (data[index].time == time) {
            return newTime1
        }
        let newTime2 = null
        if (index + 1 < data.length) {
            newTime2 = timeScale.logicalToCoordinate(index + 1 as Logical)
        }
        if (!newTime2 || !newTime1) {
            return newTime1
        }
        const fraction = (time as number - (data[index].time as number)) / ((data[index + 1].time as number) - (data[index].time as number))
        return (newTime1 + (newTime2 - newTime1) * fraction) as Coordinate
    }

    logicalToTime(index: number): Time | null {
        const data = this.getTimescaleData();
        if (index <0 || index >= data.length - 1) return null;
        return data[index].time
    }

    getPriceScaleWidth() {
        const srs = this.getSeries() as { priceScale?: () => { width: () => number } } | undefined
        if (srs && typeof srs.priceScale === 'function') {
            try {
                return srs.priceScale().width()
            } catch {
                /* series detached */
            }
        }
        return this._chartApi.priceScale('right').width()
    }

    getTimeScaleWidth() {
        return this._chartApi.timeScale().width()
    }

    formatPrice(price: number) {
        const formatter = this.getSeries().priceFormatter()
        return formatter.format(price)
    }

    formatTime(time: Time) {
        return this._chartApi.timeScale().formatDateTime(time)
    }

    getAutoscaleInfo(_startTimePoint: Logical, _endTimePoint: Logical): AutoscaleInfo | null {
        return null
    }

    updateSettings(settings: Partial<C>, publish = true) {
        this._settings = {
            ...this._settings,
            ...settings
        }
        this.onSettingsUpdate(settings)
        this.updateDrawing()
        this.onDrawingUpdate(this.id)
        publish && this._chart.publishDrawingEvent('update_settings', {
            id: this.id,
            settings,
            referenceId: this.referenceDrawingId
        }, true)
    }

    updateState(state: Partial<S>) {
        this._state = {
            ...this._state,
            ...state
        }
        this.updateDrawing()
    }

    getSettingsValues(): C {
        // override in the extended class with dynamically changing values like coordinates
        return { ...this._settings }
    }

    openDrawingSettings() {
        if (
            this.options.drawingType !== 'contractSwitch' &&
            this.options.drawingType !== 'corporateAction' &&
            this.options.drawingType !== 'companyEvent' &&
            this.options.drawingType !== 'newsArticle' &&
            this.options.drawingType !== 'chartAlertsOnChart'
        ) {
            this._chart.publishEvent(ChartEvents.OPEN_DRAWING_SETTINGS, {
                id: this.id,
                open: true
            })
        }
    }

    closeDrawingSettings() {
        this._chart.publishEvent(ChartEvents.OPEN_DRAWING_SETTINGS, {
            id: this.id,
            open: false
        })
    }

    destroy(publish = true, pushToHistory = true, chartTypeToHistory=false) {
        this._destroyed = true
        this._invalidateSeriesCache()
        const data = {
            chartId:this._chart.id,
            drawing: [{id: this.id,
                type:this.getDrawingType(),
                options: this.options,
                state: this._state,
                paneId:this._paneId,
                seriesId:this._seriesId,
                settings: this._settings,
            }]
        }
        if (pushToHistory && chartTypeToHistory) {
            this._chart.historyManager.push(data, `REMOVE DRAWING (${this.getDrawingType()})`, ActionType.REMOVE_DRAWING);
        }
        this._removeDrawModePointerListeners()
        if (this._chartApi) this._chartApi.unsubscribeCrosshairMove(this._onMouseMovePaneIndexTracking)
        this.removeMouseMoveHandler()
        if (this.primitive) {
            const series = this.getSeries()
            if (series) {
                try {
                    series.detachPrimitive(this.primitive)
                } catch (e) {
                    console.warn('DrawingBase: Failed to detach primitive during destroy', e)
                }
            }
        }
        this.removeMouseLeaveHandler()
        this.updateDrawing(false)
        this.primitive = undefined
        this.resizeObserver.disconnect()
        this.unsubscribeClick()
        this.unsubscribeDblClick()
        if (this._chartApi) this._chartApi.unsubscribeClick(this._onMouseMovePaneIndexTracking)
        this.onDestroy(this.id, publish)
    }

    // override
    getDrawingType(): DrawingTypes {
        throw `Method not defined: getDrawingType`
    }

    getDisplayType = (): DrawingDisplayType => {
        return this.displayType;
    }

    endDrawing = (): void => {
    }
    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean, doubleClick?: boolean): Point | void => {
    }
    onSettingsUpdate = (settings: Partial<C>) => {
    }
    updateHandle = (id: string, time: Time, price: number, coordinate: PointCoordinates, syncMode?: boolean) => {
    }
    recalculateHandles = () => {
    }

    loadIntialState() {
    }

    onEditEnding() {
    }

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
    }

    drawPreviewCursorDot(scope: BitmapCoordinatesRenderingScope) {
        if (!this.previewCursorPosition) return
        
        const ctx = scope.context
        const x = scaledCoordinate(this.previewCursorPosition.x, this.horizontalPixelRatio)
        const y = scaledCoordinate(this.previewCursorPosition.y, this.verticalPixelRatio)
        
        if (!x || !y) return

        ctx.beginPath()
        ctx.strokeStyle = primaryDrawingColor
        ctx.fillStyle = white
        ctx.lineWidth = HANDLER_STROKE_WIDTH * window.devicePixelRatio
        ctx.arc(x, y, HANDLER_RADIUS * window.devicePixelRatio, 0, 2 * Math.PI)
        ctx.fill()
        ctx.stroke()
        ctx.closePath()
    }

    updateHandles(points: Point[]) {
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        return null
    }

    getSyncState = (): Partial<S> => {
        return {}
    }

    _onDrawingActive() {
        this.onDrawingActive(this.getDrawingType())
    }

    onGlobalMouseUp = () => {
    }

    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => []
    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => []
    getHandles = (): Point[] => []
    getAvailableSettings = (): DrawingSettingGroup<C, SettingValues>[] => []
    getMiniSettings = (): (keyof C)[] => []
}

export default DrawingBase